/** @jsx jsx */
import React, { useState, useEffect } from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { HorizontalBarChart } from '@atlassian/viz-platform-charts--next/charts';
import { element, useElementProps } from '@atlassian/viz-platform-charts--next/core';
import { fg } from '@atlassian/jira-feature-gating';

// The right most label in the y-axis can extend outside of the chart area if it is positioned at the edge of the chart.
// Whether this occurs or not depends on values present in the chart (i.e. use value 100 with all other values being less than 10).
// Fixing this would require changes in AVP, so for now the simplest solution is to add some right padding and clip any excess.
// Labels should never be more than 4 characters long, 1000 is shown as 1K, 1000000 is shown as 1M, so we just need to account for this.
const layoutStyles = css({
	overflowX: 'clip',
	paddingRight: token('space.200'),
});

const paddingRight = 16; // space.200 === 16px

const chartStyles = css({
	display: 'flex',
	minWidth: '0',
	minHeight: '0',
	transition: 'opacity .3s ease-in-out',
	aspectRatio: '4 / 3',
});

// Source https://bitbucket.org/atlassian/atlassian-visualization/src/main/src/primitives/container/layout.tsx
export const CustomLayout = element(
	() => {
		const { children, width, height } = useElementProps(HorizontalBarChart.elements.Layout);

		const [fadeInOpacity, setFadeInOpacity] = useState(0);
		useEffect(() => {
			let isMounted = true;

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const ref = window.requestAnimationFrame(() => {
				// TODO: would be nice to fade in without triggering another render
				if (isMounted) setFadeInOpacity(1);
			});
			return () => {
				isMounted = false;

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.cancelAnimationFrame(ref);
			};
		}, [setFadeInOpacity]);

		return (
			<>
				<div
					data-element="layout"
					// eslint-disable-next-line jira/react/no-style-attribute
					style={{
						width: width
							? width - (fg('jira_nin_issue_field_statistics_fixes') ? paddingRight : 0)
							: undefined,
						height,
					}}
					css={fg('jira_nin_issue_field_statistics_fixes') && layoutStyles}
				>
					<div
						data-element="chart"
						// eslint-disable-next-line jira/react/no-style-attribute
						style={{
							opacity: fadeInOpacity,
						}}
						css={chartStyles}
					>
						{children}
					</div>
				</div>
			</>
		);
	},
	{ as: HorizontalBarChart.elements.Layout },
);
